import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gauge, AlertTriangle, ClipboardList, Sparkles, Rocket, RefreshCw, TrendingUp, Quote } from "lucide-react";

/**
 * YusiVisibilityCycleSection
 * ----------------------------------------------------------
 * Mascot-led replacement for the old "Çalışan görünürlük döngüsü" cards.
 * Architecture supports 7 steps; phase 1 uses real Yusi images for steps 1–5
 * and `placeholder: true` for steps 6–7 (final images swapped in later).
 *
 * Interaction:
 *  - Hovering or clicking any step on desktop updates the visible Yusi image
 *    and speech bubble via `activeStep` state.
 *  - On mobile, tap selects the step (hover does nothing useful on touch).
 *  - AnimatePresence handles crossfade between Yusi images + speech bubbles.
 */

const STEPS = [
  {
    n: "01",
    title: "Ölçerim",
    desc: "İşletmenizin AI görünürlük skorunu ve dijital hazırlık seviyesini çıkarırım.",
    image: "/yusi/yusi-01-focused.png",
    bubble: "Önce işletmenizin AI görünürlüğünü dikkatlice ölçerim.",
    icon: Gauge,
    en: { title: "I measure", desc: "I check your AI visibility score and your digital readiness.", bubble: "First, I carefully measure how your business appears in AI answers." },
  },
  {
    n: "02",
    title: "Eksikleri bulurum",
    desc: "Sitenizde, Google işletme profilinizde ve dijital varlığınızda yapay zekâların zor anlayacağı boşlukları bulurum.",
    image: "/yusi/yusi-02-alert-concerned.png",
    bubble: "Burada bazı eksikler görüyorum. Bunları senin için netleştiririm.",
    icon: AlertTriangle,
    en: { title: "I find gaps", desc: "I identify missing signals across your website, business profile, and digital presence.", bubble: "I can see where AI tools may struggle to understand your business." },
  },
  {
    n: "03",
    title: "Aksiyona çeviririm",
    desc: "Eksikleri teknik rapor olarak bırakmam; net Yusi Aksiyonlarına dönüştürürüm.",
    image: "/yusi/yusi-03-working-determined.png",
    bubble: "Eksikleri karışık bırakmam; onları net aksiyonlara dönüştürürüm.",
    icon: ClipboardList,
    en: { title: "I turn them into actions", desc: "I turn findings into clear, doable Yusi Actions instead of a confusing report.", bubble: "I don't leave you with a confusing report. I turn gaps into clear actions." },
  },
  {
    n: "04",
    title: "İçerikleri hazırlarım",
    desc: "Her aksiyon için kullanıma hazır çıktıyı (Google açıklaması, SSS, Hakkımızda, hizmet metni…) sizin için hazırlarım.",
    image: "/yusi/yusi-04-helpful-cheerful.png",
    bubble: "Hazır içerikleri sizin için hazırlarım; siz sadece kontrol edersiniz.",
    icon: Sparkles,
    en: { title: "I prepare outputs", desc: "For each action I prepare a ready-to-use output (Google description, FAQ, About Us, service text…).", bubble: "I prepare the outputs for you. You just review them." },
  },
  {
    n: "05",
    title: "Onayınıza sunarım",
    desc: "Hazır çıktıları inceleyin: onaylayın, düzenleyin veya reddedin. Karar her zaman sizin.",
    image: "/yusi/yusi-05-encouraging.png",
    bubble: "Karar her zaman sizde. Onaylayabilir, düzenleyebilir veya reddedebilirsiniz.",
    icon: Rocket,
    en: { title: "I ask for approval", desc: "Review the prepared outputs: approve, edit or reject. You stay in control.", bubble: "The decision is always yours. You can approve, edit or reject." },
  },
  {
    n: "06",
    title: "Kullandıklarınızı takip ederim",
    desc: "Kullandığınız çıktıları işaretleyin; yeniden tarama ile gelişiminizi takip ederim.",
    image: "/yusi/yusi-06-checking-analytical.png",
    bubble: "Kullandıklarınızı işaretleyin; gelişiminizi yeniden taramayla görürüm.",
    icon: RefreshCw,
    en: { title: "I track what you use", desc: "Mark the outputs you used — I follow your progress through rescans.", bubble: "Mark what you used and I'll track your progress with a rescan." },
  },
  {
    n: "07",
    title: "İzin verdiğiniz alanlarda ilerlemeye hazırlanırım",
    desc: "Gelecekte bağlantı verdiğiniz hesaplarda, sadece izin verdiğiniz işleri yapmaya hazırlanırım.",
    image: "/yusi/yusi-07-celebrating-yuppi.png",
    bubble: "Gelecekte izin verdiğiniz alanlarda daha çok işi sizin adınıza ben üstlenebilirim.",
    icon: TrendingUp,
    en: { title: "I prepare for permission-based automation", desc: "When you grant connections in the future, I will only act within the permissions you give me.", bubble: "When you grant permissions later, I can take on more of the work for you." },
  },
];

export default function YusiVisibilityCycleSection({ lang = "tr" }) {
  const [activeStep, setActiveStep] = useState(0);
  const baseActive = STEPS[activeStep];
  // Merge EN overrides on top of the TR-default step record.
  const active = lang === "en" && baseActive.en
    ? { ...baseActive, ...baseActive.en }
    : baseActive;

  const COPY = lang === "en"
    ? {
        eyebrow: "Yusi · Your AI assistant",
        title: "Yusi does more than suggest — it prepares the work",
        subtitle: "Yusi measures your AI visibility, prioritizes gaps, generates ready-to-use outputs and brings them to you for review. You stay in control before anything is applied outside uSeen.",
        ariaPrefix: "Step",
      }
    : {
        eyebrow: "Yusi · AI Asistan",
        title: "Yusi sadece önermez, sizin için hazırlar",
        subtitle: "Yusi işletmenizin AI görünürlüğünü ölçer, eksikleri önceliklendirir, hazır içerikler üretir ve çıktıları onayınıza sunar. Dış hesaplarda işlem yapmadan önce kontrol her zaman sizdedir.",
        ariaPrefix: "Adım",
      };

  return (
    <section
      className="relative z-10 px-6 lg:px-12 py-20 border-t border-white/5 scroll-mt-24"
      data-testid="yusi-cycle-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">
            {COPY.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            {COPY.title}
          </h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto leading-relaxed">
            {COPY.subtitle}
          </p>
        </div>

        {/* Layout: Yusi stage (left/top) + steps (right/bottom) */}
        <div className="grid lg:grid-cols-[minmax(0,42%)_1fr] gap-8 lg:gap-12 items-start">
          {/* Yusi stage */}
          <YusiStage
            active={active}
            activeIndex={activeStep}
            total={STEPS.length}
            onChange={setActiveStep}
            steps={STEPS}
            lang={lang}
            ariaPrefix={COPY.ariaPrefix}
          />

          {/* Steps list */}
          <ol
            className="space-y-3"
            data-testid="yusi-steps-list"
            onMouseLeave={() => { /* sticky last hover; do nothing */ }}
          >
            {STEPS.map((s, i) => {
              const isActive = i === activeStep;
              const Icon = s.icon;
              const stepTitle = lang === "en" && s.en ? s.en.title : s.title;
              const stepDesc = lang === "en" && s.en ? s.en.desc : s.desc;
              return (
                <li key={s.n}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveStep(i)}
                    onFocus={() => setActiveStep(i)}
                    onClick={() => setActiveStep(i)}
                    className={`group w-full text-left rounded-2xl border px-4 sm:px-5 py-4 transition-all duration-200 ${
                      isActive
                        ? "border-cyan-400/40 bg-gradient-to-r from-cyan-500/[0.08] to-purple-500/[0.06] shadow-[0_0_0_1px_rgba(34,211,238,0.18),0_18px_50px_-20px_rgba(34,211,238,0.45)]"
                        : "border-white/[0.06] bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"
                    }`}
                    data-testid={`yusi-step-${i + 1}`}
                    aria-pressed={isActive}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-mono text-sm font-semibold transition-colors ${
                          isActive
                            ? "bg-cyan-500/15 border border-cyan-400/40 text-cyan-200"
                            : "bg-white/[0.04] border border-white/10 text-slate-400 group-hover:text-slate-200"
                        }`}
                      >
                        {s.n}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon
                            size={16}
                            strokeWidth={1.7}
                            className={isActive ? "text-cyan-300" : "text-slate-500"}
                          />
                          <h3 className={`font-display font-semibold text-base sm:text-lg ${isActive ? "text-white" : "text-slate-200"}`}>
                            {stepTitle}
                          </h3>
                        </div>
                        <p className={`text-sm leading-relaxed ${isActive ? "text-slate-300" : "text-slate-400"}`}>
                          {stepDesc}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

function YusiStage({ active, activeIndex, total, onChange, steps, lang = "tr", ariaPrefix = "Adım" }) {
  // Horizontal swipe gesture: track touch start/end X on the card. A swipe
  // beyond `THRESHOLD` px shifts `activeIndex` by one (clamped 0…total-1).
  // Vertical-only drags fall through so the page can still scroll normally.
  const THRESHOLD = 40;
  const touchRef = useRef({ x: 0, y: 0, active: false });

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    touchRef.current.active = false;
    // Treat as swipe only if dominantly horizontal.
    if (Math.abs(dx) < THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0 && activeIndex < total - 1) onChange(activeIndex + 1);
    else if (dx > 0 && activeIndex > 0) onChange(activeIndex - 1);
  };

  return (
    <div className="lg:sticky lg:top-24" data-testid="yusi-stage">
      <div
        className="relative rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0c0f1f] via-[#0a0c18] to-[#08091a] p-6 sm:p-8 overflow-hidden touch-pan-y select-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="yusi-stage-card"
      >
        {/* Ambient glows */}
        <div className="absolute -top-24 -right-16 w-72 h-72 bg-cyan-500/12 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-purple-500/10 blur-3xl rounded-full pointer-events-none" />

        {/* Yusi figure */}
        <div className="relative h-[260px] sm:h-[300px] lg:h-[340px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.img
              key={`yusi-${active.n}`}
              src={active.image}
              alt={`Yusi — ${active.title}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="relative h-full max-h-full w-auto object-contain drop-shadow-[0_25px_45px_rgba(34,211,238,0.18)]"
              loading="lazy"
            />
          </AnimatePresence>
        </div>

        {/* Speech bubble */}
        <div className="relative mt-2 sm:mt-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={`bubble-${active.n}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="rounded-2xl border border-cyan-400/25 bg-cyan-500/[0.06] px-5 py-4 backdrop-blur-sm"
              data-testid="yusi-speech-bubble"
            >
              <div className="flex items-start gap-3">
                <Quote size={16} className="text-cyan-300/80 mt-0.5 shrink-0" />
                <p className="text-slate-100 text-sm sm:text-base leading-relaxed">
                  {active.bubble}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Step indicator dots — also act as clickable buttons */}
        <div className="relative mt-4 flex items-center justify-center gap-1.5" data-testid="yusi-dots">
          {steps.map((s, i) => {
            const isActive = i === activeIndex;
            const dotTitle = lang === "en" && s.en ? s.en.title : s.title;
            return (
              <button
                key={s.n}
                type="button"
                onClick={() => onChange(i)}
                aria-label={`${ariaPrefix} ${i + 1}: ${dotTitle}`}
                aria-current={isActive ? "true" : undefined}
                className={`h-1.5 rounded-full transition-all duration-200 hover:bg-cyan-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50 ${
                  isActive ? "w-6 bg-cyan-300" : "w-2 bg-white/20"
                }`}
                data-testid={`yusi-dot-${i + 1}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
